import React from 'react';

import { Autocomplete, Stack, TextField } from '@mui/material';
import { DateRange, DateRangePicker, PickersShortcutsItem } from '@mui/x-date-pickers-pro';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import { GeoFilter } from '~/services/ExperiencesService';
import { Dayjs, getDayJs } from '~/services/TimeService';

export type CountryOption = { label: string; iso2: string };
const COUNTRY_OPTIONS: Array<CountryOption> = getCountryObjs()
  .map((country) => ({ label: country.countryName, iso2: country.iso2 }))
  .sort((left, right) => left.label.localeCompare(right.label));

type Props = {
  dateRange: DateRange<Dayjs>;
  selectedCountry: CountryOption | null;
  geoFilter: GeoFilter;
  regionOptions: Array<string>;
  cityOptions: Array<string>;
  regionLoading: boolean;
  cityLoading: boolean;
  maxDate?: Dayjs;
  onDateRangeChange: (value: DateRange<Dayjs>) => void;
  onCountryChange: (event: unknown, value: CountryOption | null) => void;
  onRegionChange: (event: unknown, value: string | null) => void;
  onCityChange: (event: unknown, value: string | null) => void;
};

export default function HomepageMetricsFilters({
  dateRange,
  selectedCountry,
  geoFilter,
  regionOptions,
  cityOptions,
  regionLoading,
  cityLoading,
  maxDate = getDayJs().subtract(1, 'day'),
  onDateRangeChange,
  onCountryChange,
  onRegionChange,
  onCityChange,
}: Props) {
  const shortcuts: Array<PickersShortcutsItem<DateRange<Dayjs>>> = [7, 30, 90].map((days) => ({
    label: `Last ${days} Days`,
    getValue: () => [maxDate.subtract(days - 1, 'day'), maxDate],
  }));

  return (
    <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
      <DateRangePicker
        value={dateRange}
        onChange={onDateRangeChange}
        maxDate={maxDate}
        localeText={{ start: 'Start date', end: 'End date' }}
        slotProps={{ shortcuts: { items: shortcuts }, textField: { size: 'small' } }}
      />
      <Autocomplete
        options={COUNTRY_OPTIONS}
        value={selectedCountry}
        onChange={onCountryChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.iso2 === value.iso2}
        renderInput={(params) => <TextField {...params} label="Country" size="small" />}
        sx={{ minWidth: 200 }}
        clearOnEscape
      />
      <Autocomplete
        options={regionOptions}
        value={geoFilter.region ?? null}
        onChange={onRegionChange}
        loading={regionLoading}
        renderInput={(params) => <TextField {...params} label="State / Region" size="small" />}
        sx={{ minWidth: 220 }}
        disabled={!selectedCountry}
        clearOnEscape
      />
      <Autocomplete
        options={cityOptions}
        value={geoFilter.city ?? null}
        onChange={onCityChange}
        loading={cityLoading}
        renderInput={(params) => <TextField {...params} label="City" size="small" />}
        sx={{ minWidth: 180 }}
        disabled={!geoFilter.region}
        clearOnEscape
      />
    </Stack>
  );
}
